import { lazy, Suspense, useEffect, useRef, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AppShell } from '@/components/appShell/appShell';
import type { TabId } from '@/components/tabBar/tabBar';
import { useAuth } from '@/auth/authContext';
import { ExploreScreen } from '@/screens/explore/explore';
import { useUnreadCount } from '@/screens/messages/useUnreadCount';
import { useUnseenReportAnswers } from '@/screens/report/useUnseenReportAnswers';
import { useUnreadNotifications } from '@/screens/notifications/useUnreadNotifications';
import { playCaptureFlash } from '@/components/captureFlash/captureFlash';
import { setPendingCapture } from '@/services/pendingCapture';
import { captureForReport, warmUpCapture } from '@/services/screenCapture';

/**
 * Every screen except Keşfet is loaded when its route is first opened.
 *
 * Keşfet stays a static import because it IS "/" — deferring the landing screen
 * would only add a round trip before the thing everyone came for. It also owns
 * the map, so OpenLayers (324 KB, the largest single dependency) is on the
 * critical path either way; what these splits remove is the other twelve
 * screens, which a visitor reading listings never opens at all.
 *
 * The `.then` unwrapping is because React.lazy expects a module whose `default`
 * is the component, and these screens are named exports.
 */
const ListingDetailScreen = lazy(() =>
  import('@/screens/listingDetail/listingDetail').then((m) => ({ default: m.ListingDetailScreen }))
);
const ProducerProfileScreen = lazy(() =>
  import('@/screens/producerProfile/producerProfile').then((m) => ({
    default: m.ProducerProfileScreen,
  }))
);
const CreateListingScreen = lazy(() =>
  import('@/screens/createListing/createListing').then((m) => ({ default: m.CreateListingScreen }))
);
const ProfileScreen = lazy(() =>
  import('@/screens/profile/profile').then((m) => ({ default: m.ProfileScreen }))
);
const NotificationsScreen = lazy(() =>
  import('@/screens/notifications/notifications').then((m) => ({
    default: m.NotificationsScreen,
  }))
);
const SettingsScreen = lazy(() =>
  import('@/screens/settings/settings').then((m) => ({ default: m.SettingsScreen }))
);
const LoginScreen = lazy(() =>
  import('@/screens/auth/login').then((m) => ({ default: m.LoginScreen }))
);
const RegisterScreen = lazy(() =>
  import('@/screens/auth/register').then((m) => ({ default: m.RegisterScreen }))
);
const GoogleSignupScreen = lazy(() =>
  import('@/screens/auth/googleSignup').then((m) => ({ default: m.GoogleSignupScreen }))
);
const VerifyPhoneScreen = lazy(() =>
  import('@/screens/auth/verifyPhone').then((m) => ({ default: m.VerifyPhoneScreen }))
);
const VerifyEmailScreen = lazy(() =>
  import('@/screens/auth/verifyEmail').then((m) => ({ default: m.VerifyEmailScreen }))
);
const ForgotPasswordScreen = lazy(() =>
  import('@/screens/auth/forgotPassword').then((m) => ({ default: m.ForgotPasswordScreen }))
);
const ResetPasswordScreen = lazy(() =>
  import('@/screens/auth/resetPassword').then((m) => ({ default: m.ResetPasswordScreen }))
);
const ReportScreen = lazy(() =>
  import('@/screens/report/report').then((m) => ({ default: m.ReportScreen }))
);
const ReportListScreen = lazy(() =>
  import('@/screens/report/reportList').then((m) => ({ default: m.ReportListScreen }))
);
const InboxScreen = lazy(() =>
  import('@/screens/messages/inbox').then((m) => ({ default: m.InboxScreen }))
);
const ConversationScreen = lazy(() =>
  import('@/screens/messages/conversation').then((m) => ({ default: m.ConversationScreen }))
);

/** Route map for the shell tabs. Routes are English (technical naming); UI labels stay Turkish via tr.ts. */
const tabRoutes: Record<TabId, string> = {
  explore: '/',
  chat: '/messages',
  createListing: '/create-listing',
  report: '/report',
  profile: '/profile',
};

/**
 * Auth entry screens are pointless for a signed-in user — bounce to explore.
 * /verify-email and /reset-password stay outside: they're entered from email
 * links and must work regardless of session state.
 */
function GuestOnly({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === 'loading') return null; // one frame at boot; avoids flashing the wrong screen
  if (status === 'authed') return <Navigate to="/" replace />;
  return children;
}

/**
 * Authed-only screens; guests bounce to /login.
 *
 * The cached user is re-checked as the screen opens because it lives in memory:
 * a tab loaded hours ago still says "signed in" long after the server has
 * forgotten the session, and without this the user is handed a form whose
 * submit is guaranteed to be refused.
 */
function RequireAuth({ children }: { children: ReactNode }) {
  const { status, revalidate } = useAuth();
  useEffect(() => {
    revalidate();
    // Mount only: re-running per render would probe on every state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (status === 'loading') return null;
  if (status !== 'authed') return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // The notification list and Ayarlar are reached from Profil, so they keep Profil highlighted.
  // Both Bildir screens (the form and Bildirimlerim) belong to the Bildir tab.
  const activeTab =
    pathname === '/notifications' || pathname === '/settings'
      ? 'profile'
      : pathname.startsWith('/report/')
        ? 'report'
        : (Object.keys(tabRoutes) as TabId[]).find((t) => tabRoutes[t] === pathname);
  // detail screens replace the mobile tab bar with their contact footer (mockups)
  const isDetailRoute = pathname.startsWith('/listing/') || pathname.startsWith('/producer/');
  // an open thread owns the bottom of the screen with its composer
  const isThreadRoute = /^\/messages\/[^/]+$/.test(pathname);
  const unreadCount = useUnreadCount();
  const reportBadge = useUnseenReportAnswers();
  const notificationBadge = useUnreadNotifications();

  // Safari renders the first foreignObject capture blank; pay for it here.
  useEffect(() => warmUpCapture(), []);

  /** Bildir carries a picture of the screen being left, so the capture happens before the route does. */
  const goToTab = async (tab: TabId) => {
    if (tab !== 'report' || pathname === tabRoutes.report) {
      navigate(tabRoutes[tab]);
      return;
    }
    const shot = await captureForReport();
    if (shot) {
      setPendingCapture(shot);
      await playCaptureFlash(shot);
    }
    navigate(tabRoutes.report);
  };

  /**
   * Where the create screen was opened from, so its x returns there. Not
   * navigate(-1): a cold open of /create-listing (deep link, refresh) has no
   * in-app entry behind it and back would leave the app. Explore is the fallback
   * when no origin was recorded.
   */
  const createOrigin = useRef<string | null>(null);
  const prevPath = useRef(pathname);
  useEffect(() => {
    if (pathname !== prevPath.current) {
      if (pathname === tabRoutes.createListing) createOrigin.current = prevPath.current;
      prevPath.current = pathname;
    }
  }, [pathname]);

  const exitCreate = () => {
    const back = createOrigin.current;
    createOrigin.current = null;
    navigate(back && back !== tabRoutes.createListing ? back : tabRoutes.explore);
  };

  return (
    <AppShell
      active={activeTab}
      onSelect={goToTab}
      onExitCreate={exitCreate}
      chatBadge={unreadCount}
      reportBadge={reportBadge}
      profileBadge={notificationBadge}
      showTabBar={!isDetailRoute && !isThreadRoute}
    >
      {/* The fallback is null, not a spinner: a route chunk is a few KB from a
          cache-immutable URL, so on anything but a cold first visit it resolves
          within a frame or two and a spinner would only flash. GuestOnly and
          RequireAuth already render null on the same reasoning. */}
      <Suspense fallback={null}>
        <Routes>
          <Route
            path="/"
            element={<ExploreScreen onCreateListing={() => navigate('/create-listing')} />}
          />
          <Route path="/listing/:id" element={<ListingDetailScreen />} />
          <Route path="/producer/:id" element={<ProducerProfileScreen />} />
          <Route
            path="/login"
            element={
              <GuestOnly>
                <LoginScreen />
              </GuestOnly>
            }
          />
          <Route
            path="/register"
            element={
              <GuestOnly>
                <RegisterScreen />
              </GuestOnly>
            }
          />
          <Route
            path="/register/google"
            element={
              <GuestOnly>
                <GoogleSignupScreen />
              </GuestOnly>
            }
          />
          {/* Outside GuestOnly: verifying issues the session, and the redirect
            away from a guest-only route would race the navigation. */}
          <Route path="/register/verify" element={<VerifyPhoneScreen />} />
          <Route
            path="/forgot-password"
            element={
              <GuestOnly>
                <ForgotPasswordScreen />
              </GuestOnly>
            }
          />
          <Route path="/reset-password" element={<ResetPasswordScreen />} />
          <Route path="/verify-email" element={<VerifyEmailScreen />} />
          <Route
            path="/messages"
            element={
              <RequireAuth>
                <InboxScreen />
              </RequireAuth>
            }
          />
          <Route
            path="/messages/:id"
            element={
              <RequireAuth>
                <ConversationScreen />
              </RequireAuth>
            }
          />
          <Route
            path="/create-listing"
            element={
              <RequireAuth>
                <CreateListingScreen />
              </RequireAuth>
            }
          />
          <Route
            path="/create-listing/:id"
            element={
              <RequireAuth>
                <CreateListingScreen />
              </RequireAuth>
            }
          />
          <Route
            path="/report"
            element={
              <RequireAuth>
                <ReportScreen />
              </RequireAuth>
            }
          />
          <Route
            path="/report/mine"
            element={
              <RequireAuth>
                <ReportListScreen />
              </RequireAuth>
            }
          />
          <Route
            path="/profile"
            element={
              <RequireAuth>
                <ProfileScreen />
              </RequireAuth>
            }
          />
          <Route
            path="/notifications"
            element={
              <RequireAuth>
                <NotificationsScreen />
              </RequireAuth>
            }
          />
          <Route
            path="/settings"
            element={
              <RequireAuth>
                <SettingsScreen />
              </RequireAuth>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AppShell>
  );
}
